import { useToast } from '@/composables/useToast';
import type { Customer } from '@/data/types';
import i18n from '@/i18n';
import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { stripePaymentMethodId } from '@/lib/stripeBilling';
import { useUserStore } from '@/stores/user';
import type {
  Plan as BillingPlan,
  Invoice,
  Offerings as OfferingsData,
  PaymentMethod,
  PlanData,
  PlanProduct,
  Subscription,
} from '@/types/billing';
import { RestError } from '@dabble/rest-client';
import { defineStore } from 'pinia';
import type { Stripe } from 'stripe';
import { computed, ref } from 'vue';
// Assume we have API functions similar to the Svelte rest calls
// import { fetchBillingDataApi, fetchOfferingsApi } from '@/api/billing';
// For now, we'll use placeholders

// Define interfaces based on Stripe types (or simplified versions)
interface StripeCustomer extends Stripe.Customer {
  // Add other relevant customer fields
}

/** Translate a key using the global i18n instance — for use outside Vue components. */
function tStore(key: string): string {
  return (i18n as unknown as { global: { t: (key: string) => string } }).global.t(key);
}

const TERMINAL_SUB_STATUSES = new Set(['canceled', 'incomplete_expired']);

const PRIMARY_SUB_PRIORITY: Subscription['status'][] = [
  'active',
  'trialing',
  'past_due',
  'paused',
  'unpaid',
  'incomplete',
];

/** Prefer a billable subscription when Stripe returns multiple rows (e.g. newest canceled + older active). */
export function pickPrimarySubscription(subs: Subscription[]): Subscription | null {
  if (!subs?.length) return null;
  const candidates = subs.filter((s) => !TERMINAL_SUB_STATUSES.has(s.status as string));
  if (!candidates.length) return null;
  for (const status of PRIMARY_SUB_PRIORITY) {
    const found = candidates.find((s) => s.status === status);
    if (found) return found;
  }
  return candidates[0];
}

/** Top-level `plan` can be absent on item/price-based subscriptions; derive from first line item. */
export function resolveSubscriptionPlan(sub: Subscription): Subscription['plan'] | undefined {
  const existing = sub.plan;
  if (existing && (existing as { id?: string }).id) return existing;
  const raw = sub as unknown as {
    items?: {
      data?: Array<{
        plan?: Subscription['plan'];
        price?: Stripe.Price & { product?: Stripe.Product | string };
      }>;
    };
  };
  const item = raw.items?.data?.[0];
  if (item?.plan) return item.plan;
  const price = item?.price;
  if (price && typeof price === 'object') {
    const product = price.product;
    const productObj =
      typeof product === 'object' && product && 'id' in product ? (product as Stripe.Product) : undefined;
    return {
      id: price.id,
      name: productObj?.name ?? '',
      amount: price.unit_amount,
      currency: price.currency ?? 'usd',
      interval: (price.recurring?.interval as string) ?? 'month',
      product: productObj
        ? { id: productObj.id, name: productObj.name }
        : { id: typeof product === 'string' ? product : price.id },
      description: productObj?.description ?? undefined,
    } as Subscription['plan'];
  }
  return existing;
}

export const useBillingStore = defineStore('billing', () => {
  // --- State ---
  const { showToast } = useToast();

  const customer = ref<StripeCustomer | null>(null);
  const subscriptions = ref<Subscription[]>([]);
  const paymentMethods = ref<PaymentMethod[]>([]);
  const plans = ref<BillingPlan[]>([]);
  const invoices = ref<Invoice[]>([]);
  const upcomingInvoice = ref<Stripe.Invoice | null>(null);
  const selectedPlan = ref<PlanData | null>(null);
  const selectedPlanId = ref<string | null>(null);
  const offerings = ref<OfferingsData | null>(null);
  const hasTrialAvailable = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const selection = ref<'month' | 'year'>('month');
  const product = ref<PlanProduct | null>(null);
  const error = ref<string | null>(null); // Store error messages as strings
  const lastApplyError = ref<string | null>(null);
  const userStore = useUserStore();

  // --- Actions ---
  async function fetchBillingOverview(uid?: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const billingData = (await rest
        .get('/billing/data')
        .header('x-uid', uid || userStore.user?.uid || '')
        .credentials('include')
        .send()) as {
        customer: Stripe.Customer;
        subscriptions: Subscription[];
        paymentMethods: PaymentMethod[];
        invoices: Invoice[];
        offerings: OfferingsData;
        hasTrialAvailable?: boolean;
        plans?: BillingPlan[];
      };

      userStore.setStripeId(billingData.customer.id);
      customer.value = billingData.customer as StripeCustomer;
      subscriptions.value = billingData.subscriptions;
      paymentMethods.value = billingData.paymentMethods as unknown as PaymentMethod[];
      invoices.value = billingData.invoices;
      if (Array.isArray(billingData.plans)) {
        plans.value = billingData.plans;
      }
      offerings.value = billingData.offerings as OfferingsData;
      hasTrialAvailable.value = Boolean(billingData.hasTrialAvailable);
    } catch (err) {
      logError('Failed to fetch billing overview:', err);
      const message =
        err instanceof RestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to fetch billing overview';
      if (message.includes('NO_BILLING_ACCOUNT')) {
        error.value = null;
        subscriptions.value = [];
        customer.value = null;
        try {
          offerings.value = (await rest
            .get('/billing/offerings/current')
            .credentials('include')
            .send()) as OfferingsData;
        } catch {
          // offerings unavailable — leave null
        }
        return;
      }
      error.value = message;
      showToast('danger', message);
    } finally {
      isLoading.value = false;
    }
  }

  async function retryInvoicePayment(invoiceId: string): Promise<Invoice | null> {
    try {
      const updated = (await rest
        .post(`/billing/invoices/${invoiceId}/pay`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send()) as Invoice;
      invoices.value = invoices.value.map((i) => (i.id === invoiceId ? updated : i));
      // Successful retry flips the subscription from past_due → active; refresh so UI clears.
      if (updated?.status === 'paid') {
        await fetchBillingOverview();
      }
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to retry payment';
      error.value = message;
      showToast('danger', message);
      return null;
    }
  }

  async function fetchInvoices(after?: string): Promise<Invoice[]> {
    isLoading.value = true;
    error.value = null;
    let result: Invoice[] = [];
    try {
      let req = rest
        .get('/billing/invoices')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include');
      if (after) req = req.query('after', after);
      result = (await req.send()) as Invoice[];
      if (!after) invoices.value = result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch invoices';
      error.value = message;
      showToast('danger', message);
    } finally {
      isLoading.value = false;
    }
    return result;
  }

  async function getCustomer(uid: string, stripeId?: string) {
    try {
      const response = await rest
        .get('/billing/data')
        .header('x-uid', uid || '')
        .query('stripeId', stripeId || '')
        .credentials('include')
        .send();
      return response.customer;
    } catch (err) {
      return null;
    }
  }

  /** POST /billing/customer only — no trial. Updates local `user.stripeId` on success. */
  async function createStripeCustomerOnly({
    uid,
    email,
    name,
  }: {
    uid: string;
    email: string;
    name: string;
  }): Promise<string | false> {
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Partial<Customer> = { uid, email, name };
      const response = await rest
        .post('/billing/customer')
        .header('x-uid', uid || '')
        .credentials('include')
        .send(payload);
      const stripeId = response.stripeId as string;
      userStore.setStripeId(stripeId);
      return stripeId;
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Failed to create billing customer';
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Creates Stripe customer and optionally starts the trial (`POST /billing/trial`).
   * @param withTrial When true (default), chains trial creation after customer creation.
   */
  async function createCustomer({
    uid,
    email,
    name,
    withTrial = true,
  }: {
    uid: string;
    email: string;
    name: string;
    withTrial?: boolean;
  }): Promise<string | false> {
    const stripeId = await createStripeCustomerOnly({ uid, email, name });
    if (!stripeId || !withTrial) return stripeId;

    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .post('/billing/trial')
        .header('x-uid', uid || '')
        .credentials('include')
        .send();
      showToast('success', tStore('billing_trial_started'));
      return stripeId;
    } catch (err: any) {
      if (err.message === 'SUBSCRIPTION_EXISTS') return stripeId;

      const message = err instanceof Error ? err.message : 'Failed to start trial';
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /** Existing Stripe customer, no trial yet — `POST /billing/trial` (server ensures customer + trial). */
  async function startTrialSubscription(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .post('/billing/trial')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      await fetchBillingOverview();
      showToast('success', tStore('billing_trial_started'));
      return true;
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Failed to start trial';
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function pauseBillingSubscription(resumeDateUnixSeconds: number): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .put('/billing/subscription/pause')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send({ paused: resumeDateUnixSeconds });
      await fetchBillingOverview();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pause subscription';
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function unpauseBillingSubscription(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .delete('/billing/subscription/pause')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      await fetchBillingOverview();
      return true;
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to resume subscription';
      const message = raw === 'PAUSE_LOCKED' ? tStore('billing_pause_lock_error_locked') : raw;
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function setDefaultPaymentMethod(paymentMethodId: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .post(`/billing/payment-methods/${paymentMethodId}/default`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      await fetchBillingOverview();
      showToast('success', tStore('billing_payment_method_default_updated'));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set default payment method';
      error.value = message;
      showToast('danger', message);
      // Revert optimistic update on error if needed
    } finally {
      isLoading.value = false;
    }
  }

  async function removePaymentMethod(paymentMethodId: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .delete(`/billing/payment-methods/${paymentMethodId}`)
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send();
      // Optimistic update or fetch
      paymentMethods.value = paymentMethods.value.filter((pm) => pm.id !== paymentMethodId);
      await fetchBillingOverview();
      showToast('success', tStore('billing_payment_method_removed'));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove payment method';
      error.value = message;
      showToast('danger', message);
      // Revert optimistic update on error if needed
    } finally {
      isLoading.value = false;
    }
  }

  // --- New Action: Create Subscription ---
  async function createSubscription(plan: BillingPlan, paymentMethodId?: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const payload: { plan: BillingPlan; paymentMethodId?: string } = { plan };
      if (paymentMethodId) {
        payload.paymentMethodId = paymentMethodId;
      }
      // Call the API to create the subscription
      const response = await rest
        .post('/billing/subscription')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send(payload);

      if (response) {
        // Add the new subscription to the local state
        subscriptions.value.push(response.subscription);
        // Refresh the billing overview for consistency
        await fetchBillingOverview();
        showToast('success', response.message || tStore('billing_subscription_created'));
        return true; // Indicate success
      } else {
        throw new Error(response.message || 'Failed to create subscription');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred while subscribing.';
      error.value = message;
      showToast('danger', message);
      return false; // Indicate failure
    } finally {
      isLoading.value = false;
    }
  }

  async function resumeSubscription(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;
    try {
      await rest
        .put('/billing/subscription')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send({});
      await fetchBillingOverview();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume subscription';
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // --- Cancel Subscription ---
  async function cancelSubscription(
    options: {
      reason?: string;
      feedback?: string;
      otherReason?: string;
      missingFeatures?: string;
      alternativeName?: string;
      technicalIssues?: string;
      when?: 'now' | 'end_of_period';
    } = {},
  ) {
    isLoading.value = true;
    error.value = null;
    try {
      const { when, ...rest_ } = options;
      const response = await rest
        .delete('/billing/subscription')
        .header('x-uid', userStore.user?.uid || '')
        .credentials('include')
        .send(when === 'now' ? { ...rest_, when: 'now' } : rest_);

      if (response?.success !== false) {
        await fetchBillingOverview();
        showToast('success', response?.message || tStore('billing_subscription_updated'));
        return true;
      }
      throw new Error(response?.message || 'Failed to cancel subscription');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred during cancellation.';
      error.value = message;
      showToast('danger', message);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  const BILLING_ERROR_I18N: Record<string, string> = {
    COUPON_INVALID: 'billing_error_COUPON_INVALID',
    COUPON_EXPIRED: 'billing_error_COUPON_EXPIRED',
    COUPON_UNREDEEMABLE: 'billing_error_COUPON_UNREDEEMABLE',
    COUPON_NOT_VALID: 'billing_error_COUPON_NOT_VALID',
    REFERRAL_NEW_ACCOUNTS_ONLY: 'billing_error_REFERRAL_NEW_ACCOUNTS_ONLY',
    COUPON_INVALID_MULTIPLE_PERCENT: 'billing_error_COUPON_INVALID_MULTIPLE_PERCENT',
  };

  function translateBillingApiError(message: string): string {
    const i18nKey = BILLING_ERROR_I18N[message] ?? 'billing_coupon_error_generic';
    return tStore(i18nKey);
  }

  /**
   * Validates the display code via `POST /billing/coupons/:code`, then applies it with
   * `PUT /billing/subscription` { coupon: id }. Stripe is only touched on the server.
   *
   * `silent: true` suppresses the success/error toasts so callers can render their
   * own UI; the localized error string is still exposed via `lastApplyError`.
   */
  async function applyCouponCode(displayCode: string, options: { silent?: boolean } = {}): Promise<boolean> {
    const trimmed = displayCode.trim();
    if (!trimmed) return false;
    const { silent = false } = options;

    isLoading.value = true;
    error.value = null;
    lastApplyError.value = null;
    const uid = userStore.user?.uid || '';
    try {
      const validated = await rest
        .post(`/billing/coupons/${encodeURIComponent(trimmed)}`)
        .header('x-uid', uid)
        .credentials('include')
        .send();

      const couponId =
        validated && typeof validated === 'object' && 'id' in validated ? (validated as { id: string }).id : undefined;
      if (!couponId) {
        const msg = translateBillingApiError('COUPON_INVALID');
        error.value = msg;
        lastApplyError.value = msg;
        if (!silent) showToast('danger', msg);
        return false;
      }

      await rest.put('/billing/subscription').header('x-uid', uid).credentials('include').send({ coupon: couponId });

      await fetchBillingOverview();
      if (!silent) showToast('success', tStore('billing_coupon_applied'));
      return true;
    } catch (err) {
      const raw = err instanceof RestError ? err.message : err instanceof Error ? err.message : 'UNKNOWN';
      const msg = translateBillingApiError(raw);
      error.value = msg;
      lastApplyError.value = msg;
      if (!silent) showToast('danger', msg);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchPlan(planId: string) {
    const response = await rest
      .get(`/billing/plans/${planId}`)
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include')
      .send();
    return response;
  }

  /** Preview of the next invoice (`GET /billing/invoices/upcoming`). Returns null if none or on error. */
  async function fetchUpcomingInvoice(): Promise<Stripe.Invoice | null> {
    const uid = userStore.user?.uid || '';
    try {
      const invoice = (await rest
        .get('/billing/invoices/upcoming')
        .header('x-uid', uid)
        .credentials('include')
        .send()) as Stripe.Invoice | null;
      const resolved = invoice?.id ? invoice : null;
      upcomingInvoice.value = resolved;
      return resolved;
    } catch (err) {
      logError('fetchUpcomingInvoice', err);
      upcomingInvoice.value = null;
      return null;
    }
  }
  // --- End New Action ---

  // --- Getters (Computed Properties) ---
  const hasActiveSubscription = computed(() => {
    return subscriptions.value && subscriptions.value.some((sub) => sub.status === 'active');
  });

  const defaultPaymentMethod = computed<Stripe.PaymentMethod | null>(() => {
    const c = customer.value as Stripe.Customer | null;
    const raw = c?.invoice_settings?.default_payment_method;
    if (raw == null) return null;
    if (typeof raw === 'object' && raw !== null && 'id' in raw) {
      return raw as Stripe.PaymentMethod;
    }
    const id = stripePaymentMethodId(raw);
    return id ? ({ id } as Stripe.PaymentMethod) : null;
  });

  const activeSubscription = computed(() => {
    return subscriptions.value.find((sub) => sub.status === 'active');
  });

  const primarySubscription = computed<Subscription | null>(() => {
    const sub = pickPrimarySubscription(subscriptions.value);
    if (!sub) return null;
    const plan = resolveSubscriptionPlan(sub);
    if (plan && plan !== sub.plan) {
      return { ...sub, plan };
    }
    return sub;
  });

  const isPausedSubscription = computed(() => {
    const sub = primarySubscription.value;
    if (!sub) return false;
    if (sub.status === 'paused') return true;
    if (sub.pause_collection?.behavior) return true;
    const until = sub.metadata?.is_paused_until;
    if (until != null && until !== '') return true;
    return false;
  });

  // Subscription metadata stamped by the backend at pause time. Self-service unpause is gated until this date.
  // Missing metadata = pre-lock pause, grandfathered (no lock applies).
  const pauseLockedUntil = computed<number | null>(() => {
    const raw = primarySubscription.value?.metadata?.pause_locked_until;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  });

  const isPauseLocked = computed(() => {
    const until = pauseLockedUntil.value;
    return until != null && Date.now() / 1000 < until;
  });

  const isTrialing = computed(() => primarySubscription.value?.status === 'trialing' && !isPausedSubscription.value);

  const isActivePaid = computed(() => primarySubscription.value?.status === 'active' && !isPausedSubscription.value);

  const isDelinquent = computed(() => {
    const status = primarySubscription.value?.status;
    return status === 'past_due' || status === 'unpaid';
  });

  const latestOpenInvoice = computed<Invoice | null>(() => {
    return invoices.value.find((i) => i.status === 'open') ?? null;
  });

  const isSubscribedDuringTrial = computed(() => {
    const sub = primarySubscription.value;
    if (!sub) return false;
    if (sub.status !== 'trialing') return false;
    if (isPausedSubscription.value) return false;
    if (sub.cancel_at_period_end) return false;
    return defaultPaymentMethod.value !== null;
  });

  const hasStripeCustomer = computed(() => Boolean(userStore.user?.stripeId));

  return {
    // State
    customer,
    subscriptions,
    paymentMethods,
    plans,
    invoices,
    upcomingInvoice,
    selectedPlan,
    selectedPlanId,
    offerings,
    hasTrialAvailable,
    isLoading,
    error,
    lastApplyError,

    // Actions
    fetchBillingOverview,
    setDefaultPaymentMethod,
    removePaymentMethod,
    fetchInvoices,
    retryInvoicePayment,
    createCustomer,
    createStripeCustomerOnly,
    startTrialSubscription,
    pauseBillingSubscription,
    unpauseBillingSubscription,
    getCustomer,
    cancelSubscription,
    resumeSubscription,
    createSubscription,
    applyCouponCode,
    fetchPlan,
    fetchUpcomingInvoice,

    // Getters
    hasActiveSubscription,
    defaultPaymentMethod,
    activeSubscription,
    primarySubscription,
    isPausedSubscription,
    pauseLockedUntil,
    isPauseLocked,
    isTrialing,
    isActivePaid,
    isDelinquent,
    latestOpenInvoice,
    isSubscribedDuringTrial,
    hasStripeCustomer,
    selection,
    product,
  };
});
